// components/RouteErrorBoundary.js
import React from 'react';
import { 
  Paper, 
  Typography, 
  Button, 
  Box,
  Collapse,
  IconButton 
} from '@material-ui/core';
import { 
  Error as ErrorIcon, 
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
  },
  errorPaper: {
    padding: theme.spacing(4),
    maxWidth: 600,
    width: '100%',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  errorTitle: {
    marginBottom: theme.spacing(2),
  },
  errorMessage: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
  detailsContainer: {
    marginTop: theme.spacing(2),
    textAlign: 'left',
  },
  errorDetails: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    overflow: 'auto',
    maxHeight: 200,
  },
  toggleButton: {
    marginTop: theme.spacing(2),
  }
}));

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      showDetails: false
    };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    console.error('Route scheduling error:', error, errorInfo);
    this.setState({ errorInfo });
    
    // Send to error tracking service if available
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack
          }
        }
      });
    }
  }
  
  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false 
    });
  };
  
  handleRefresh = () => {
    window.location.reload();
  };
  
  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };
  
  render() {
    if (this.state.hasError) {
      return <ErrorDisplay 
        error={this.state.error}
        errorInfo={this.state.errorInfo}
        showDetails={this.state.showDetails}
        onReset={this.handleReset}
        onRefresh={this.handleRefresh}
        onToggleDetails={this.toggleDetails}
      />;
    }
    
    return this.props.children;
  }
}

const ErrorDisplay = ({ 
  error, 
  errorInfo, 
  showDetails, 
  onReset, 
  onRefresh, 
  onToggleDetails 
}) => {
  const classes = useStyles();
  
  return (
    <Box className={classes.errorContainer}>
      <Paper className={classes.errorPaper} elevation={3}>
        <ErrorIcon className={classes.errorIcon} />
        
        <Typography variant="h5" className={classes.errorTitle}>
          Oops! Something went wrong
        </Typography>
        
        <Typography variant="body1" className={classes.errorMessage}>
          We encountered an error while loading the route scheduler. 
          This has been logged and we'll look into it.
        </Typography>
        
        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
          >
            Refresh Page
          </Button>
          
          <Button
            variant="outlined"
            onClick={onReset}
          >
            Try Again
          </Button>
        </Box>
        
        {error && (
          <>
            <Button
              className={classes.toggleButton}
              onClick={onToggleDetails}
              endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
            >
              {showDetails ? 'Hide' : 'Show'} Technical Details
            </Button>
            
            <Collapse in={showDetails}>
              <Box className={classes.detailsContainer}>
                <Typography variant="subtitle2" gutterBottom>
                  Error Message:
                </Typography>
                <Box className={classes.errorDetails}>
                  {error.toString()}
                  {errorInfo && (
                    <>
                      {'\n\nComponent Stack:'}
                      {errorInfo.componentStack}
                    </>
                  )}
                </Box>
              </Box>
            </Collapse>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default RouteErrorBoundary;
